// Turning whatever Giraffe sends into DataRow[].
//
// Every state key arrives in more than one shape: `bakedSections` is TYPED
// `Record<id, Section[]>` and arrives as a GeoJSON FeatureCollection; `rawSections`
// is a Record from the state key but an array from `getProjectBundle`. So this
// tries every shape rather than trusting the declared type.

import type { DataRow } from '@/data/types';

interface FeatureLike {
  id?: unknown;
  type?: unknown;
  geometry?: { type?: string; coordinates?: unknown } | null;
  properties?: Record<string, unknown> | null;
}

/**
 * Accepts a FeatureCollection, an array of features, or a Record of either
 * features or arrays of features, and flattens all of them.
 */
export function normalizeFeatures(input: unknown): DataRow[] {
  const features = collectFeatures(input);
  const rows: DataRow[] = [];
  const seen = new Map<string, number>();
  for (const f of features) {
    const row = toRow(f, seen);
    if (row) rows.push(row);
  }
  return rows;
}

function collectFeatures(input: unknown): FeatureLike[] {
  if (input == null) return [];
  if (Array.isArray(input)) return input.flatMap((x) => collectFeatures(x));
  if (typeof input !== 'object') return [];

  const obj = input as Record<string, unknown>;
  if (Array.isArray(obj.features)) return obj.features as FeatureLike[];
  if (obj.type === 'Feature' || 'geometry' in obj || 'properties' in obj) return [obj as FeatureLike];

  // A Record keyed by feature id, whose values are features or arrays of them.
  const out: FeatureLike[] = [];
  for (const value of Object.values(obj)) out.push(...collectFeatures(value));
  return out;
}

function toRow(f: FeatureLike, seen: Map<string, number>): DataRow | null {
  const props = (f.properties ?? {}) as Record<string, unknown>;
  const rawId = f.id ?? props.id;
  const id = rawId == null ? '' : String(rawId);
  if (id === '' && Object.keys(props).length === 0) return null;

  /*
   * Baked-section ids are NOT unique. Every copy of an instanced block carries
   * the block DEFINITION's `properties.id`, so a flow that stamps one block down
   * a line delivers N sections under one id. `id` is a valid key for a writeback
   * and an invalid key for a collection — a Map keyed by it keeps one instance
   * and silently drops the rest. So the collection key is id + a geometry
   * signature, and the bare id stays available for writes.
   */
  const sig = geometrySignature(f.geometry);
  let key = `${id}#${sig}`;
  // Two instances can still coincide exactly (a block stamped twice in place).
  const n = seen.get(key);
  if (n === undefined) {
    seen.set(key, 1);
  } else {
    seen.set(key, n + 1);
    key = `${key}@${n}`;
  }

  return { key, id, properties: props };
}

/**
 * A cheap, stable fingerprint of a geometry: vertex count plus the first and
 * last positions at ~1 cm precision. Enough to tell two stamped instances apart
 * and stable enough that an unmoved instance keeps its identity across rebuilds.
 */
export function geometrySignature(geometry: FeatureLike['geometry']): string {
  if (!geometry || geometry.coordinates == null) return 'nogeom';
  const positions: number[][] = [];
  let count = 0;
  const walk = (node: unknown, depth: number): void => {
    if (!Array.isArray(node)) return;
    if (typeof node[0] === 'number') {
      count += 1;
      if (positions.length < 1) positions.push(node as number[]);
      positions[1] = node as number[];
      return;
    }
    if (depth > 6) return;
    for (const child of node) walk(child, depth + 1);
  };
  walk(geometry.coordinates, 0);
  const fmt = (p: number[] | undefined): string =>
    p ? p.slice(0, 3).map((v) => (typeof v === 'number' ? v.toFixed(7) : '?')).join(',') : '-';
  return `${geometry.type ?? '?'}:${count}:${fmt(positions[0])}:${fmt(positions[1])}`;
}

/** Bounding box of a feature's geometry, for flyTo/fitBounds. */
export function boundsOf(geometry: FeatureLike['geometry']): [[number, number], [number, number]] | null {
  if (!geometry || geometry.coordinates == null) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const walk = (node: unknown, depth: number): void => {
    if (!Array.isArray(node)) return;
    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      const [x, y] = node as [number, number];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      return;
    }
    if (depth > 6) return;
    for (const child of node) walk(child, depth + 1);
  };
  walk(geometry.coordinates, 0);
  if (!Number.isFinite(minX)) return null;
  return [
    [minX, minY],
    [maxX, maxY],
  ];
}

/**
 * `getProjectUsages` returns a bag of usages whose numbers live inside
 * `default` and `join` — top-level fields are ignored by Giraffe and are
 * usually absent. Flatten both onto one row so a sheet can read them.
 */
export function normalizeUsages(input: unknown): DataRow[] {
  if (input == null || typeof input !== 'object') return [];
  const out: DataRow[] = [];
  for (const [name, value] of Object.entries(input as Record<string, unknown>)) {
    if (value == null || typeof value !== 'object') continue;
    const usage = value as { default?: Record<string, unknown>; join?: Record<string, unknown> };
    const properties: Record<string, unknown> = {
      name,
      usage: name,
      ...(usage.default ?? {}),
      ...(usage.join ?? {}),
    };
    // Deliberately NOT given a synthetic `area`. It made a weighted mean behave,
    // but it also put a meaningless "Area 1" column in the usage table, and a
    // fabricated field is worse than an honest absence. A single-value group
    // needs no weighting anyway — see aggregate.ts.
    out.push({ key: `usage:${name}`, id: name, properties });
  }
  return out;
}

/**
 * The drawing layers the project offers, by the name a feature's `layerId`
 * carries.
 *
 * Read off `layerTree`, not `projectLayers`: `projectLayers` lists basemaps and
 * vector-tile layers too, and their ids are the numeric project-layer
 * namespace, which is not the namespace a section's `layerId` ("default", "2")
 * lives in. Offering those as places to put a feature would be offering buckets
 * nothing can ever sit in.
 *
 * Giraffe registers one mapbox layer per drawing layer, with a tree id like
 * `GIRDRW-1:default-fill` — the app id, the layer name, and how it is drawn. The
 * name in the middle is the `layerId`, so the prefix and the geometry suffix
 * come off. Items that name a layer outright (`layerId`, `drawingLayerId`) are
 * taken as they are.
 *
 * Best-effort by construction: `layerTree`'s shape is undocumented, so this
 * returns what it recognises and the Features sheet unions it with the layers
 * its own features are already on.
 *
 * Returned in the tree's own STACKING order, not alphabetised — now that
 * `sortRows` (entityTable.ts) uses it to order Features/Areas rows by
 * layer. A caller that wants an alphabetical list for a dropdown
 * (`layerOptions` in featureTable.ts/areasTable.ts) already sorts its own
 * combined list afterwards, so nothing here depends on this being
 * pre-sorted.
 *
 * Confirmed live as the actual bug behind "layerId isn't sorting": the
 * tree does NOT arrive nested — it is `{ items: Record<id, { children?:
 * id[] }>, rootId }`, a FLAT dictionary of nodes, each one only naming its
 * children BY ID, not containing them. A generic deep walk over this
 * object sees `items` as just another record and visits its entries in
 * whatever order the dictionary itself was built in — which is not the
 * tree's own order, since a group's children are flat siblings of
 * everything else in `items`, not nested under it. The real stacking order
 * only exists by actually following `children` arrays from `rootId`,
 * below.
 */
const DRAW_SUFFIX = /-(fill|line|stroke|symbol|circle|extrusion|outline|point|label|text|3d)$/i;

function nameOf(out: Set<string>, obj: Record<string, unknown>): void {
  const named = (value: unknown): void => {
    if (typeof value === 'string' && value.trim() !== '') out.add(value.trim());
  };
  named(obj.layerId);
  named(obj.drawingLayerId);
  if (typeof obj.id === 'string') {
    const id = obj.id;
    if (id.startsWith('GIRDRW-')) {
      const colon = id.indexOf(':');
      if (colon >= 0) {
        const name = id.slice(colon + 1).replace(DRAW_SUFFIX, '').trim();
        if (name !== '') out.add(name);
      }
    }
  }
}

/**
 * The real shape: `items[id]` is a node, `items[id].children` an array of
 * CHILD IDS (not nested objects) to look up in `items` again, and
 * `rootId` names where to start. Walked depth-first, each node's own name
 * taken before its children's, same visual order as a layer panel reading
 * top to bottom.
 */
function walkTree(layerTree: Record<string, unknown>, out: Set<string>): boolean {
  const items = layerTree.items;
  const rootId = layerTree.rootId;
  if (items === null || typeof items !== 'object' || typeof rootId !== 'string') return false;
  const table = items as Record<string, unknown>;
  const seen = new Set<string>();
  const visit = (id: string, depth: number): void => {
    if (depth > 64 || seen.has(id)) return;
    seen.add(id);
    const node = table[id];
    if (node === null || typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    nameOf(out, obj);
    const children = obj.children;
    if (Array.isArray(children)) {
      for (const child of children) if (typeof child === 'string') visit(child, depth + 1);
    }
  };
  visit(rootId, 0);
  return true;
}

/**
 * Deep, and guarded against cycles rather than by a shallow ceiling —
 * `walkTree`'s fallback for a `layerTree` that does not match the
 * `items`/`rootId` shape above (the stated shape is undocumented).
 *
 * The depth counter ticks for every level of nesting, arrays included, so a
 * tree of groups burns two levels per visible row — the old ceiling of 6 ran
 * out inside a project with nested layer groups and the walk returned nothing
 * at all. A `seen` set is what makes going deeper safe: a tree whose nodes
 * carry a parent back-reference would otherwise revisit for ever. Does NOT
 * reconstruct a reliable stacking order — flat dictionaries and `children`
 * id-arrays read in whatever order the object itself was built in — so it
 * is only reached when the real tree walk above cannot run at all.
 */
function walkGeneric(node: unknown, out: Set<string>, seen: WeakSet<object>, depth: number): void {
  if (node == null || typeof node !== 'object' || depth > 24) return;
  if (seen.has(node)) return;
  seen.add(node);
  if (Array.isArray(node)) {
    for (const child of node) walkGeneric(child, out, seen, depth + 1);
    return;
  }
  const obj = node as Record<string, unknown>;
  nameOf(out, obj);
  for (const [key, value] of Object.entries(obj)) {
    nameOf(out, { id: key });
    walkGeneric(value, out, seen, depth + 1);
  }
}

export function drawingLayerNames(layerTree: unknown): string[] {
  const out = new Set<string>();
  const structured = layerTree !== null && typeof layerTree === 'object' && !Array.isArray(layerTree) && walkTree(layerTree as Record<string, unknown>, out);
  if (!structured || out.size === 0) walkGeneric(layerTree, out, new WeakSet<object>(), 0);
  return [...out];
}

/**
 * Same walk as `walkTree`, but also records which names it saw while a node
 * (or an ancestor — hiding a GROUP hides everything under it) was marked
 * `data.visible: false`. A name drawn by more than one mapbox sub-layer
 * (`-fill`/`-line`/…) counts as visible if ANY of them was — failing open
 * is the safer guess for a filter than hiding a layer that was never
 * actually hidden on a signal nobody has fully confirmed (see
 * `visibleDrawingLayerNames` below).
 */
function walkVisibility(layerTree: Record<string, unknown>, visible: Set<string>, hidden: Set<string>): boolean {
  const items = layerTree.items;
  const rootId = layerTree.rootId;
  if (items === null || typeof items !== 'object' || typeof rootId !== 'string') return false;
  const table = items as Record<string, unknown>;
  const seen = new Set<string>();
  const visit = (id: string, depth: number, ancestorHidden: boolean): void => {
    if (depth > 64 || seen.has(id)) return;
    seen.add(id);
    const node = table[id];
    if (node === null || typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    const nodeData = obj.data;
    const ownHidden =
      nodeData !== null && typeof nodeData === 'object' && (nodeData as Record<string, unknown>).visible === false;
    const hiddenHere = ancestorHidden || ownHidden;
    const names = new Set<string>();
    nameOf(names, obj);
    for (const name of names) (hiddenHere ? hidden : visible).add(name);
    const children = obj.children;
    if (Array.isArray(children)) {
      for (const child of children) if (typeof child === 'string') visit(child, depth + 1, hiddenHere);
    }
  };
  visit(rootId, 0, false);
  return true;
}

/**
 * `drawingLayerNames()`, minus whatever the layer panel has switched off.
 *
 * `layerTree`'s `data.visible` is undocumented and confirmed only against
 * one captured shape (see the describe block above) — not against a live
 * project. A layer this cannot read a visibility bit for (no `items`/
 * `rootId` to walk, or simply never mentioned) is assumed visible, same as
 * `drawingLayerNames` would show it: this never HIDES a layer on a guess,
 * only on an explicit `visible: false` it actually found.
 */
export function visibleDrawingLayerNames(layerTree: unknown): string[] {
  const all = drawingLayerNames(layerTree);
  if (layerTree === null || typeof layerTree !== 'object' || Array.isArray(layerTree)) return all;
  const visible = new Set<string>();
  const hidden = new Set<string>();
  const structured = walkVisibility(layerTree as Record<string, unknown>, visible, hidden);
  if (!structured) return all;
  return all.filter((name) => visible.has(name) || !hidden.has(name));
}
